import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Block } from '@/components/Block'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import {
  useSubChannelAllocationsList,
  useUpdateSubChannelAllocations,
} from '@/hooks/queries/crt/useSubChannelAllocations'
import { useLastUpdatedSubChannelAllocationsLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { Button } from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SubChannelAllocationsDataTable from './-components/SubChannelAllocationsDataTable'
import { toModel, toViewModel } from './-utils/subChannelAllocationViewModel'

export const Route = createFileRoute(
  '/_auth/_modules/crt/volume-forecasts/sub-channel-allocations/',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation('crt')

  const [editedValues, setEditedValues] = useState<Map<number, boolean>>(new Map())

  const { data, isPending, error } = useSubChannelAllocationsList()
  const { data: lastUpdatedTrail } = useLastUpdatedSubChannelAllocationsLog()

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTrail)

  const { mutate: updateAllocations, isPending: isSaving } = useUpdateSubChannelAllocations({
    handleOnSuccess: () => {
      setEditedValues(new Map())
    },
  })

  const viewModels = data?.results ? toViewModel(data.results) : []

  const originalValueById = new Map<number, boolean>(
    data?.results?.map((allocation) => [allocation.id, allocation.isAllowed]) ?? [],
  )

  const handleToggle = useCallback(
    (id: number, currentValue: boolean) => {
      setEditedValues((prev) => {
        const newMap = new Map(prev)
        const newValue = !currentValue
        if (newValue === originalValueById.get(id)) {
          newMap.delete(id)
        } else {
          newMap.set(id, newValue)
        }
        return newMap
      })
    },
    [originalValueById],
  )

  const getDisplayValue = useCallback(
    (id: number, originalValue: boolean): boolean => {
      if (editedValues.has(id)) {
        return editedValues.get(id)!
      }
      return originalValue
    },
    [editedValues],
  )

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return
    const updates = toModel(viewModels, editedValues)
    updateAllocations({ updates })
  }

  const areChangesPending = editedValues.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread>
        <PageHeader>{t('subChannelAllocations.pageHeader')}</PageHeader>
        <Button type="button" variant="secondary" asChild>
          <Link to="/crt/volume-forecasts">
            <ArrowLeftIcon className="size-4" />
            {t('actions.backToVolumeForecasts')}
          </Link>
        </Button>
      </Spread>

      <SubChannelAllocationsDataTable
        data={viewModels}
        isPending={isPending}
        error={error}
        editedValues={editedValues}
        getDisplayValue={getDisplayValue}
        onToggle={handleToggle}
      />

      <PermissionsGuard required={['crt_volume_forecasts_configure']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          lastUpdatedLog={lastUpdatedLog}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
